import { normalizeHttpError } from "../../../lib/http/errors";
import { FulfillmentOrderUserFacingError } from "../types";

export { FulfillmentOrderUserFacingError } from "../types";

export const MAX_FULFILLMENT_ARCHIVE_BYTES = 20 * 1024 * 1024;
export const FULFILLMENT_ARCHIVE_ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

const allowedArchiveMimeTypes = new Set(["application/pdf", "image/png", "image/jpeg"]);
const allowedArchiveExtensions = new Set(["pdf", "png", "jpg", "jpeg"]);
const trustedSanitizedMessages = new Set([
  "快速录入上下文暂不可用",
]);
const internalActionMessagesByCode: Readonly<Record<string, string>> = {
  FULFILLMENT_ORDER_REVISION_CONFLICT: "确认件已被其他人更新，请刷新后重试。",
  FULFILLMENT_ORDER_INVALID_LINE: "确认行信息不完整或不符合要求，请检查后重试。",
  FULFILLMENT_ORDER_COMPANY_PARTY_NOT_CONFIGURED:
    "协议甲方主体尚未配置，请联系管理员完成系统配置。",
  FULFILLMENT_ORDER_GENERATION_CONFLICT: "确认件生成状态已发生变化，请刷新后重试。",
  FULFILLMENT_ORDER_AGREEMENT_EFFECTIVE:
    "该结算主体的服务协议已生效，请选择新的结算主体。",
  FULFILLMENT_ORDER_ARCHIVE_INVALID: "盖章协议文件不符合要求，请重新选择。",
};

/** 清理业务文案中的错误码、链路标识和堆栈，只保留可执行的中文说明。 */
export function sanitizeFulfillmentOrderBusinessMessage(
  rawMessage: string | null | undefined,
  fallback: string,
): string {
  const message = (rawMessage ?? "")
    .split(/\r?\n/u)
    .filter((line) => !/^\s*at\s+/iu.test(line))
    .join(" ")
    .replace(/[（(]\s*[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\s*[）)]/gu, "")
    .replace(/(?:错误代码|错误码|后端错误码|Trace\s*ID|堆栈)[：:]\s*[^；。，\n]*/giu, "")
    .replace(/\b(?:code|trace(?:\s*id)?|stack)\b\s*[：:=]\s*[^；。，\s]+/giu, "")
    .replace(/\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b\s*[：:]?/gu, "")
    .replace(/[（(]\s*[）)]/gu, "")
    .replace(/\s*([，。；、])\s*/gu, "$1")
    .replace(/([，；])\1+/gu, "$1")
    .replace(/^[：:，。；、\s]+|[：:，；、\s]+$/gu, "")
    .trim();
  return trustedSanitizedMessages.has(message) ? message : fallback;
}

/** 内部动作只保留可信中文业务原因，其他异常使用调用方提供的稳定文案。 */
export function getSafeFulfillmentOrderActionErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof FulfillmentOrderUserFacingError) return error.message;
  const looseCode = error && typeof error === "object" ? Reflect.get(error, "code") : null;
  const normalizedCode = normalizeHttpError(error).code;
  const code = typeof looseCode === "string" ? looseCode : normalizedCode;
  return code ? internalActionMessagesByCode[code] ?? fallback : fallback;
}

/** 协议或工作区切换后旧动作完成时使用的静默错误。 */
export class FulfillmentOrderStaleActionError extends Error {
  constructor() {
    super("stale-fulfillment-order-action");
    this.name = "FulfillmentOrderStaleActionError";
  }
}

/** 判断错误是否表示应静默忽略的旧页面动作。 */
export function isFulfillmentOrderStaleActionError(
  error: unknown,
): error is FulfillmentOrderStaleActionError {
  return error instanceof FulfillmentOrderStaleActionError;
}

/** 将 generated 供应商契约收口为页面安全模型。 */
/** 校验盖章归档文件类型和 20 MiB 上限。 */
export function isSupportedArchiveFile(file: File): boolean {
  const normalizedType = file.type.trim().toLowerCase();
  const extension = file.name.split(".").at(-1)?.toLowerCase() ?? "";
  const typeSupported = allowedArchiveMimeTypes.has(normalizedType)
    || ((!normalizedType || normalizedType === "application/octet-stream")
      && allowedArchiveExtensions.has(extension));
  return file.size > 0
    && file.size <= MAX_FULFILLMENT_ARCHIVE_BYTES
    && typeSupported;
}

/** 返回归档文件的中文校验结果。 */
export function validateArchiveFile(file: File | null): string | null {
  if (!file) return "请选择盖章文件";
  if (file.size <= 0) return "文件内容不能为空";
  if (file.size > MAX_FULFILLMENT_ARCHIVE_BYTES) return "文件不能超过 20 MiB";
  if (!isSupportedArchiveFile(file)) {
    return "仅支持 PDF、PNG 或 JPEG 文件";
  }
  return null;
}
